import FormSaveButton from "@babackapp/react-hook-forms-components/form-save-button";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Form } from "@babackapp/ui/components/form";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@babackapp/ui/components/tabs";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import AddressTab from "./address-tab";
import OpeningTimesTab from "./opening-time-tab";
import { StoreFormSchema, StoreFormValues } from "./types";

type StoreFormProps = {
  defaultValues?: Partial<StoreFormValues>;
  onSubmit: (store: StoreFormValues) => void;
  onRemove?: () => void;
};

const EMPTY_OPENING_TIMES: StoreFormValues["openingTimes"] = {
  monday: [],
  tuesday: [],
  wednesday: [],
  thursday: [],
  friday: [],
  saturday: [],
  sunday: [],
};

const DEFAULT_VALUES: Partial<StoreFormValues> = {
  general: {
    selectableByUser: true,
    externalId: "",
    address: {
      address1: "",
      address2: "",
      contactName: "",
      city: "",
      zip: "",
      companyName: "",
      country: {
        name: "France",
        code: "FR",
      },
    },
  },
  openingTimes: EMPTY_OPENING_TIMES,
};

export default function StoreForm({
  defaultValues,
  onSubmit,
  onRemove,
}: StoreFormProps) {
  const t = useTranslation();

  const form = useForm<StoreFormValues>({
    resolver: zodResolver(StoreFormSchema),
    defaultValues: {
      ...defaultValues,
      ...DEFAULT_VALUES,
      general: {
        ...DEFAULT_VALUES.general,
        ...defaultValues?.general,
        address: {
          ...DEFAULT_VALUES.general?.address,
          ...defaultValues?.general?.address,
        },
      },
      openingTimes: {
        ...EMPTY_OPENING_TIMES,
        ...defaultValues?.openingTimes,
      },
    },
  });

  const addressError = form.formState.errors?.general;
  const openingTimesError = form.formState.errors?.openingTimes;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <Tabs className="w-full" defaultValue="address">
          <TabsList
            className="w-full grid grid- grid-cols-2 mb-8"
            defaultValue="address"
          >
            <TabsTrigger
              value="address"
              className={addressError && "text-destructive"}
            >
              {t("settings.shop.physic.tabs.address")}
            </TabsTrigger>
            <TabsTrigger
              value="opening-times"
              className={openingTimesError && "text-destructive"}
            >
              {t("settings.shop.physic.tabs.openingTimes")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="address">
            <AddressTab />
          </TabsContent>

          <TabsContent value="opening-times">
            <OpeningTimesTab />
          </TabsContent>
        </Tabs>

        <div className="flex gap-2 items-center justify-between">
          {onRemove && (
            <Button
              variant="destructive-outline"
              type="button"
              onClick={onRemove}
            >
              {t("misc.delete")}
            </Button>
          )}
          <FormSaveButton />
        </div>
      </form>
    </Form>
  );
}
